// src/components/ui/DetalleNotificacion.js
//
// Pinta el detalle ESTRUCTURADO de una notificación (`datosAdicionales.detalle`)
// como un listado agrupado, en lugar del texto de corrido.
//
// 🔴 Por qué existe: el aviso "Paquetes con guía que Forza NO recolectó" llegaba
// como un párrafo que recortaba a 12 pedidos y terminaba en "y 48 más" — y "Ver
// mensaje completo" mostraba EXACTAMENTE el mismo texto recortado. Con 60 paquetes
// parados en bodega, ese aviso existe justo para ir a buscarlos: hay que verlos
// todos, agrupados por empresa, que es como se camina la bodega.
//
// Es genérico a propósito (no sabe nada de Forza), así que cualquier notificación
// futura que necesite listar muchos renglones solo tiene que guardar este shape:
//
//   datosAdicionales: {
//     detalle: {
//       intro: 'texto explicativo (sin la lista)',
//       total: 60,
//       etiquetaItems: 'pedido(s)',        // opcional
//       etiquetaSecundario: 'Guía',        // opcional, solo para lectores de pantalla
//       recortado: 12,                     // opcional: cuántos no entraron
//       grupos: [{ nombre: 'Rincón', items: [{ principal, secundario, nota }] }]
//     }
//   }
//
// Si la notificación no trae ese detalle, el componente NO pinta nada (devuelve
// null) y el modal sigue mostrando su mensaje de texto de siempre.

import React, { useState } from 'react';
import { BuildingStorefrontIcon, ClipboardIcon, CheckIcon } from './Icons';
import aviso from '../../utils/aviso';

/**
 * Arma el texto plano del listado, pensado para pegarlo en WhatsApp o imprimirlo
 * cuando alguien va a caminar la bodega. Sin emojis (regla del admin).
 */
const armarTextoPlano = (titulo, d) => {
  const etiqueta = d.etiquetaItems || 'renglón(es)';
  const cabecera = `${titulo || 'Detalle'} — ${d.total || 0} ${etiqueta}`;
  const cuerpo = (d.grupos || []).map((g) => {
    const items = (g.items || [])
      .map((it) => `- ${[it.principal, it.secundario, it.nota].filter(Boolean).join('  ·  ')}`)
      .join('\n');
    return `${String(g.nombre || '').toUpperCase()} (${(g.items || []).length})\n${items}`;
  }).join('\n\n');
  const cola = d.recortado > 0 ? `\n\ny ${d.recortado} más que no entraron en este detalle.` : '';
  return `${cabecera}\n\n${cuerpo}${cola}`;
};

/** ¿Esta notificación trae detalle estructurado para listar? */
export const tieneDetalleListado = (notif) => {
  const d = notif?.datosAdicionales?.detalle;
  return !!(d && Array.isArray(d.grupos) && d.grupos.length > 0);
};

export default function DetalleNotificacion({ notificacion }) {
  const [copiado, setCopiado] = useState(false);

  // 🔴 El hook va ANTES de cualquier return condicional (regla de React): si se
  // saliera primero, el orden de hooks cambiaría entre renders y rompería.
  if (!tieneDetalleListado(notificacion)) return null;
  const d = notificacion.datosAdicionales.detalle;
  const etiqueta = d.etiquetaItems || 'renglón(es)';

  const copiarLista = async () => {
    const texto = armarTextoPlano(notificacion.titulo, d);
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch (error) {
      // Fallback para navegadores sin permiso de portapapeles (mismo patrón que el
      // resto del sistema): textarea temporal + execCommand.
      try {
        const ta = document.createElement('textarea');
        ta.value = texto;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopiado(true);
        setTimeout(() => setCopiado(false), 2000);
      } catch (e) {
        // Un botón que no hace nada y no avisa es peor que no tenerlo.
        aviso.error('No se pudo copiar la lista. Probá seleccionarla a mano.');
      }
    }
  };

  return (
    <div>
      {d.intro && (
        <p style={{
          margin: '0 0 14px 0',
          fontSize: '14px',
          lineHeight: '1.6',
          color: 'var(--color-texto)'
        }}>
          {d.intro}
        </p>
      )}

      {/* Resumen + copiar. Sirve para pegar la lista en WhatsApp o imprimirla
          antes de ir a bodega, que es lo que se hace con este aviso. */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '12px'
      }}>
        <span style={{ fontSize: '12px', color: 'var(--color-texto-secundario)' }}>
          {d.total || 0} {etiqueta} en {d.grupos.length} {d.grupos.length === 1 ? 'empresa' : 'empresas'}
        </span>
        <button
          type="button"
          onClick={copiarLista}
          className="btn-press hover-bright"
          title="Copiar la lista completa como texto"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: copiado ? 'var(--color-exito)' : 'var(--color-primario)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radio-sm)',
            padding: '7px 14px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          {copiado
            ? <><CheckIcon className="w-4 h-4" /> ¡Copiado!</>
            : <><ClipboardIcon className="w-4 h-4" /> Copiar lista</>}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {d.grupos.map((g, gi) => (
          <div
            key={`${g.nombre}-${gi}`}
            style={{
              background: 'var(--color-card-elevado)',
              border: '1px solid var(--color-borde-card)',
              borderRadius: 'var(--radio-md)',
              overflow: 'hidden'
            }}
          >
            {/* Encabezado del grupo (empresa) */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
              padding: '9px 12px',
              background: 'var(--color-primario-suave)',
              borderBottom: '1px solid var(--color-borde-suave)'
            }}>
              <span style={{ color: 'var(--color-primario-texto)', display: 'inline-flex' }}>
                <BuildingStorefrontIcon className="w-4 h-4" />
              </span>
              <strong style={{ fontSize: '13px', color: 'var(--color-primario-texto)' }}>
                {g.nombre}
              </strong>
              <span style={{
                marginLeft: 'auto',
                fontSize: '11px',
                fontWeight: '700',
                color: 'var(--color-primario-texto)',
                background: 'var(--color-card)',
                border: '1px solid var(--color-primario-borde)',
                borderRadius: 'var(--radio-pill)',
                padding: '2px 9px',
                whiteSpace: 'nowrap'
              }}>
                {(g.items || []).length} {etiqueta}
              </span>
            </div>

            {/* Renglones del grupo */}
            <div>
              {(g.items || []).map((it, i) => (
                <div
                  key={`${it.principal}-${i}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '4px 12px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    borderTop: i === 0 ? 'none' : '1px solid var(--color-borde-suave)'
                  }}
                >
                  <strong style={{ color: 'var(--color-texto)' }}>{it.principal}</strong>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    {it.secundario && (
                      <span style={{ color: 'var(--color-primario-texto)', fontWeight: '600' }}>
                        {it.secundario}
                      </span>
                    )}
                    {it.nota && (
                      <span style={{ color: 'var(--color-texto-secundario)', fontSize: '12px' }}>
                        {it.nota}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {d.recortado > 0 && (
        <p style={{
          margin: '12px 0 0 0',
          fontSize: '12px',
          color: 'var(--color-texto-secundario)'
        }}>
          y {d.recortado} más que no entraron en este detalle.
        </p>
      )}
    </div>
  );
}
